import { useState, useEffect, useCallback } from 'react'
import * as reportsApi from '@/services/api/reports'
import type { PaginationMeta, ReportQueryParams } from '@/services/api/reports'

type TabKey = 'contracts' | 'shipments' | 'inventory' | 'stock' | 'payments' | 'supplier' | 'client' | 'product'

interface UseReportsParams {
  tab: TabKey
  page?: number
  limit?: number
  status?: string
  startDate?: string
  endDate?: string
}

interface UseReportsResult {
  data: any[]
  summary: Record<string, any>
  meta: PaginationMeta | null
  loading: boolean
  error: string | null
  errorCode: 'unauthorized' | 'forbidden' | 'network' | 'unknown' | null
  refetch: () => void
}

export function useReports(params: UseReportsParams): UseReportsResult {
  const { tab, page = 1, limit = 10, status, startDate, endDate } = params
  const [data, setData] = useState<any[]>([])
  const [summary, setSummary] = useState<Record<string, any>>({})
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<'unauthorized' | 'forbidden' | 'network' | 'unknown' | null>(null)

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      setErrorCode(null)

      const baseParams: ReportQueryParams = {
        page,
        limit,
        status: status && status !== 'Status' ? status : undefined,
        startDate,
        endDate,
      }

      let result: { data: any; meta: PaginationMeta }

      switch (tab) {
        case 'contracts': {
          result = await reportsApi.getContractReport(baseParams)
          setData(result.data.contracts ?? [])
          setSummary(result.data.summary ?? {})
          break
        }
        case 'shipments': {
          result = await reportsApi.getShipmentReport(baseParams)
          setData(result.data.shipments ?? [])
          setSummary(result.data.summary ?? {})
          break
        }
        case 'inventory': {
          result = await reportsApi.getInventoryReport(baseParams)
          setData(result.data.rows ?? result.data.lots ?? [])
          setSummary(result.data.summary ?? {})
          break
        }
        case 'stock': {
          result = await reportsApi.getStockReport(baseParams)
          setData(result.data.dispatches ?? [])
          setSummary(result.data.summary ?? {})
          break
        }
        case 'payments': {
          result = await reportsApi.getPaymentReport(baseParams)
          setData(result.data.payments ?? [])
          setSummary(result.data.summary ?? {})
          break
        }
        case 'supplier': {
          result = await reportsApi.getSupplierReport(baseParams)
          setData(result.data.suppliers ?? [])
          setSummary(result.data.summary ?? {})
          break
        }
        case 'client': {
          result = await reportsApi.getClientReport(baseParams)
          setData(result.data.clients ?? [])
          setSummary(result.data.summary ?? {})
          break
        }
        case 'product': {
          result = await reportsApi.getProductReport(baseParams)
          setData(result.data.products ?? [])
          setSummary(result.data.summary ?? {})
          break
        }
        default:
          result = { data: {}, meta: { page: 1, limit: 10, total: 0, totalPages: 0 } }
          setData([])
          setSummary({})
      }

      setMeta(result.meta)
    } catch (err: unknown) {
      const axiosError = err as any
      const httpStatus = axiosError?.response?.status

      if (httpStatus === 401) {
        setError('Your session has expired. Please log in again to view reports.')
        setErrorCode('unauthorized')
      } else if (httpStatus === 403) {
        setError('You do not have permission to view this report. Contact your administrator.')
        setErrorCode('forbidden')
      } else if (!axiosError?.response) {
        setError('Unable to connect to the server. Please check your connection and try again.')
        setErrorCode('network')
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch report')
        setErrorCode('unknown')
      }
      setData([])
      setSummary({})
      setMeta(null)
    } finally {
      setLoading(false)
    }
  }, [tab, page, limit, status, startDate, endDate])

  useEffect(() => {
    fetchReport()
  }, [fetchReport])

  return { data, summary, meta, loading, error, errorCode, refetch: fetchReport }
}
